import { useState, useMemo } from 'react';
import SprintHeader from './SprintHeader';
import SprintItemCard from './SprintItemCard';
import SprintItemModal from './SprintItemModal';
import AddItemModal from './AddItemModal';
import UserProfileModal from './UserProfileModal';
import SprintHistoryView from './SprintHistoryView';
import BacklogView from './BacklogView';
import ProjectSettingsModal from './ProjectSettingsModal';
import AddMembersModal from './AddMembersModal';
import TemplatesView from './TemplatesView';
import ConfirmModal from './ConfirmModal';
import { PlusIcon, LoaderIcon, InboxIcon } from './Icons';
import { COLUMNS, KANBAN_COLUMNS, KANBAN_STATUS_OPTIONS, STATUS_OPTIONS, RETRO_COLUMNS, PRIORITY_CONFIG, TYPE_CONFIG } from '../constants';
import { getItemBreadcrumb } from '../utils';
import useSprintBoard from '../hooks/useSprintBoard';

export default function SprintBoard({ user, userProfile, users, onLogout, showToast, refreshUsers, refreshProfile, project, onBackToProjects, onProjectUpdated, onAddMembers }) {
  const {
    sprint, items, boardItems, loading, isKanban, isRetro, projectUsers,
    handleStatusChange, handleAssigneeChange, handleChildStatusToggle, handleAddItem, handleUpdateItem, handleDeleteItem, handleUpdateSprint, handleEndSprint, reloadItems,
  } = useSprintBoard({ showToast, project, users });

  const activeColumns = isRetro ? RETRO_COLUMNS : isKanban ? KANBAN_COLUMNS : COLUMNS;

  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showBacklog, setShowBacklog] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const sortByPriority = (list) =>
    [...list].sort((a, b) => (PRIORITY_CONFIG[a.priority]?.order ?? 1) - (PRIORITY_CONFIG[b.priority]?.order ?? 1));

  const [dragOverCol, setDragOverCol] = useState(null);

  const handleColumnDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(status);
  };

  const handleColumnDragLeave = (e, status) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverCol(null);
    }
  };

  const handleColumnDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const itemId = e.dataTransfer.getData('text/plain');
    if (!itemId) return;
    const item = items.find((i) => i.id === itemId);
    if (item && item.status !== newStatus) {
      await handleStatusChange(itemId, newStatus);
    }
  };

  const onAddItem = async (itemData) => {
    try {
      await handleAddItem(isRetro ? { ...itemData, type: 'task' } : itemData);
      showToast('Item added', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to add item', 'error');
    }
  };

  const onUpdateItem = async (itemId, data) => {
    try {
      const updated = await handleUpdateItem(itemId, data);
      setSelectedItem(updated);
      showToast('Item updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update item', 'error');
    }
  };

  const onDeleteItem = async (itemId) => {
    try {
      await handleDeleteItem(itemId);
      showToast('Item deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete item', 'error');
    }
  };

  const onUpdateSprint = async (data) => {
    try {
      await handleUpdateSprint(data);
      showToast('Sprint updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update sprint', 'error');
    }
  };

  const onEndSprint = async () => {
    try {
      await handleEndSprint();
      setShowEndConfirm(false);
      showToast(isRetro ? 'Retro ended. New retro started.' : 'Sprint ended. New sprint created.', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to end sprint', 'error');
    }
  };

  const breadcrumbMap = useMemo(() => {
    const map = {};
    boardItems.forEach((item) => {
      if (item.parentId) {
        const chain = getItemBreadcrumb(item.id, items);
        if (chain.length > 0) {
          map[item.id] = chain.map((p) => {
            const c = TYPE_CONFIG[p.type];
            return `${c?.label || p.type}: ${p.title}`;
          }).join(' / ');
        }
      }
    });
    return map;
  }, [boardItems, items]);

  if (loading) {
    return (
      <div className="app-loading">
        <LoaderIcon size={32} />
        <p>Loading sprint...</p>
      </div>
    );
  }

  if (showHistory) {
    return (
      <SprintHistoryView
        onBack={() => setShowHistory(false)}
        showToast={showToast}
        users={projectUsers}
        userProfile={userProfile}
        projectId={project.id}
        projectType={project?.type}
      />
    );
  }

  if (showTemplates) {
    return (
      <TemplatesView
        onBack={() => setShowTemplates(false)}
        showToast={showToast}
        userProfile={userProfile}
        sprintId={sprint?.id}
        projectType={project?.type || 'scrum'}
        onItemsImported={() => {
          reloadItems();
          setShowTemplates(false);
        }}
      />
    );
  }

  if (showBacklog) {
    const handleBacklogUpdate = async (itemId, data) => {
      try {
        const updated = await handleUpdateItem(itemId, data);
        showToast('Item updated', 'success');
        return updated;
      } catch (err) {
        showToast(err.message || 'Failed to update item', 'error');
      }
    };
    return (
      <BacklogView
        items={items}
        onBack={() => { setShowBacklog(false); setSelectedItem(null); }}
        onUpdateItem={handleBacklogUpdate}
        onDeleteItem={onDeleteItem}
        onAddItem={onAddItem}
        showToast={showToast}
        users={projectUsers}
        userProfile={userProfile}
        projectId={project.id}
        sprint={sprint}
        isKanban={isKanban}
      />
    );
  }

  if (!sprint) return null;

  const totalPoints = boardItems.reduce((sum, i) => sum + (i.storyPoints || 0), 0);
  const donePoints = boardItems.filter((i) => i.status === 'done').reduce((sum, i) => sum + (i.storyPoints || 0), 0);

  return (
    <div className="sprint-board-wrapper">
      <SprintHeader
        sprint={sprint}
        onUpdateSprint={onUpdateSprint}
        onLogout={onLogout}
        userProfile={userProfile}
        onShowHistory={() => setShowHistory(true)}
        onEndSprint={() => setShowEndConfirm(true)}
        onShowProfile={() => setShowProfile(true)}
        project={project}
        onBackToProjects={onBackToProjects}
        onShowProjectSettings={() => setShowProjectSettings(true)}
        onShowAddMembers={onAddMembers ? () => setShowAddMembers(true) : null}
        isKanban={isKanban}
        isRetro={isRetro}
        onShowTemplates={isRetro ? null : () => setShowTemplates(true)}
      />

      <div className="sprint-board-toolbar">
        {!isRetro && (
          <div className="sprint-points-summary">
            <span>{donePoints} / {totalPoints} pts</span>
            {totalPoints > 0 && (
              <div className="sprint-progress-bar">
                <div className="sprint-progress-fill" style={{ width: `${(donePoints / totalPoints) * 100}%` }} />
              </div>
            )}
          </div>
        )}
        <div className="sprint-board-toolbar-actions">
          {!isRetro && (
            <button className="btn btn-backlog btn-sm" onClick={() => setShowBacklog(true)}>
              <InboxIcon size={16} /> Backlog
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
            <PlusIcon size={16} /> {isRetro ? 'Add Note' : 'Add Item'}
          </button>
        </div>
      </div>

      <div className="sprint-board" id="sprint-board-export" style={{ '--board-cols': activeColumns.length }}>
        {activeColumns.map((col) => {
          const colItems = boardItems.filter((i) => i.status === col.status);
          const sortedItems = isKanban ? sortByPriority(colItems) : colItems;
          return (
            <div
              key={col.status}
              className={`sprint-column sprint-column-${col.status}${dragOverCol === col.status ? ' sprint-column--drag-over' : ''}`}
              onDragOver={(e) => handleColumnDragOver(e, col.status)}
              onDragLeave={(e) => handleColumnDragLeave(e, col.status)}
              onDrop={(e) => handleColumnDrop(e, col.status)}
            >
              <div className="sprint-column-header">
                <h3>{col.title}</h3>
                <span className="sprint-column-count">{sortedItems.length}</span>
              </div>
              <div className="sprint-column-body">
                {sortedItems.map((item) => (
                  <SprintItemCard
                    key={item.id}
                    item={item}
                    onStatusChange={handleStatusChange}
                    onAssigneeChange={handleAssigneeChange}
                    onChildStatusToggle={handleChildStatusToggle}
                    onClick={setSelectedItem}
                    users={projectUsers}
                    showPriority={isKanban}
                    breadcrumbMap={breadcrumbMap}
                    allItems={items}
                    isRetro={isRetro}
                    statusOptions={isKanban ? KANBAN_STATUS_OPTIONS : STATUS_OPTIONS}
                  />
                ))}
                {colItems.length === 0 && (
                  <div className="sprint-column-empty">No items</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedItem && (
        <SprintItemModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={onUpdateItem}
          onDelete={onDeleteItem}
          users={projectUsers}
          isKanban={isKanban}
          isRetro={isRetro}
          allItems={items}
        />
      )}

      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onAdd={onAddItem}
          users={projectUsers}
          isKanban={isKanban}
          isRetro={isRetro}
        />
      )}

      {showEndConfirm && (
        <ConfirmModal
          message={isRetro
            ? 'End this retrospective? All notes will be saved to history.'
            : 'End this sprint? Completed items will be archived. Uncompleted items will move to the next sprint\'s backlog.'}
          onConfirm={onEndSprint}
          onCancel={() => setShowEndConfirm(false)}
          confirmLabel={isRetro ? 'End Retro' : 'End Sprint'}
        />
      )}

      {showProfile && (
        <UserProfileModal
          userProfile={userProfile}
          onClose={() => setShowProfile(false)}
          onProfileUpdated={() => { refreshProfile(); refreshUsers(); }}
          showToast={showToast}
        />
      )}

      {showProjectSettings && (
        <ProjectSettingsModal
          project={project}
          onClose={() => setShowProjectSettings(false)}
          onProjectUpdated={(updated) => { onProjectUpdated(updated); setShowProjectSettings(false); }}
          onProjectDeleted={() => { setShowProjectSettings(false); onBackToProjects(); }}
          users={users}
          showToast={showToast}
        />
      )}

      {showAddMembers && onAddMembers && (
        <AddMembersModal
          project={project}
          users={users}
          onAdd={onAddMembers}
          onClose={() => setShowAddMembers(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
